import {
  ClientEditSVG,
  DropdownButtonChevronDown,
  DropdownButtonChevronUp,
  EyeSVG,
  InputSearchIconSVG,
  PlusIconSVG,
  ProjectAddMoreTimeSVG,
  ProjectDeleteSVG,
  ProjectExtendTimeSVG,
} from "@/assets/svg";
import { FilterSelectWithRadio } from "@/components/form";
import AppLayout from "@/components/layout/AppLayout";
import Modal from "@/components/Modal";
import NoDataContainer from "@/components/NoDataContainer";
import Tooltip from "@/components/Tooltip";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { selectUserRole } from "@/store/slices/authSlice";
import {
  fetchClientOptions,
  selectClientOptions,
} from "@/store/slices/clientSlice";
import {
  addProject,
  deleteProject,
  fetchProjects,
  getProjectDetail,
  resetFilters,
  selectClientId,
  selectFilteredProjects,
  selectNoProjects,
  selectPagination,
  selectProjectLoading,
  selectSearchTerm,
  selectStatusFilter,
  selectWarningFilter,
  selectProjectTypeFilter,
  setClientId,
  setSearchTerm,
  setStatusFilter,
  setWarningFilter,
  setProjectTypeFilter,
  setSort,
  updateProject,
  selectSortBy,
  selectSortOrder,
} from "@/store/slices/projectSlice";
import { selectSelectedOrganization } from "@/store/slices/clientOrganizationSlice";
import { ProjectList, ProjectQuery } from "@/types/project.model";
import { capitalizeFirstLetter, ROLES } from "@/utils/constants";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { debounce } from "lodash";
import moment from "moment";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import AddMoreTimeModal from "./components/AddMoreTimeModal";
import ExtendEndDateModal from "./components/ExtendEndDateModal";
import ProjectForm, { ProjectFormData } from "./components/ProjectForm";

// Define column meta type for TypeScript
type ColumnMeta = {
  isSticky?: boolean;
  width?: string;
};

// Define status badge styles for different project statuses
const getStatusStyles = (
  status: string
): { bgColor: string; textColor: string } => {
  let bgColor = "bg-[#DCF1FE]";
  let textColor = "text-[#007FFF]";

  switch (status) {
    case "active":
      bgColor = "bg-[#EDFFF9]";
      textColor = "text-[#0C7453]";
      break;
    case "inactive":
      bgColor = "bg-[#FFEDED]";
      textColor = "text-[#FF0000]";
      break;
    case "On Hold":
      bgColor = "bg-[#FFF8ED]";
      textColor = "text-[#D88C0C]";
      break;
    case "Cancelled":
      bgColor = "bg-[#FFEDF0]";
      textColor = "text-[#FF0000]";
      break;
  }

  return { bgColor, textColor };
};

// Hours display component with color coding
const HoursDisplay: React.FC<{
  hours: number | string;
  isNegative?: boolean;
}> = ({ hours, isNegative = false }) => {
  const textColor = isNegative ? "text-red-500" : "";

  if (hours === null || hours === undefined) {
    return <span className={textColor}>-</span>;
  }

  if (typeof hours === "string") {
    const t = hours.trim();
    if (t === "" || t === "-") {
      return <span className={textColor}>-</span>;
    }
    // List API often sends full label already, e.g. "56:00 hrs (56.00)" — do not append " hrs"
    if (/\bhrs\b/i.test(t)) {
      return <span className={textColor}>{hours}</span>;
    }
  }

  const displayHours = `${typeof hours === "number" ? hours.toFixed(2) : hours} hrs`;
  return <span className={textColor}>{displayHours}</span>;
};

/** List API may return `esti_hrs` as a formatted string (e.g. `"56:00 hrs (56.00)"`) or `"-"` for N/A. */
const renderEstiHoursCell = (value: unknown) => {
  if (value === null || value === undefined || value === "") return "-";
  return <HoursDisplay hours={value as number | string} />;
};

// Sortable Header Component
const SortableHeader: React.FC<{
  title: string;
  sortKey: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSort: (key: string) => void;
}> = ({ title, sortKey, sortBy, sortOrder, onSort }) => {
  return (
    <div
      className="flex w-[fit-content] items-center gap-1 cursor-pointer select-none group"
      onClick={() => onSort(sortKey)}
    >
      {title}
      <div className="flex flex-col">
        <DropdownButtonChevronUp
          width={20}
          height={20}
          fill="black"
          className="mb-[-4px]"
        />
        <DropdownButtonChevronDown
          width={20}
          height={20}
          fill="black"
          className="mt-[-4px]"
        />
      </div>
    </div>
  );
};

// Project table component using TanStack React Table
const ProjectTable: React.FC<{
  data: ProjectList[];
  onEdit: (projectId: string) => void;
  onView: (projectId: string) => void;
  onDelete: (projectId: string) => void;
  onExtend: (projectId: string) => void;
  onAddTime: (projectId: string) => void;
  pagination: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    pageSize: number;
  };
  onPageChange: (page: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSort: (key: string) => void;
}> = ({
  data,
  onEdit,
  onView,
  onDelete,
  onExtend,
  onAddTime,
  pagination,
  onPageChange,
  sortBy,
  sortOrder,
  onSort,
}) => {
    const columnHelper = createColumnHelper<ProjectList>();
    const userRole = useSelector(selectUserRole);
    const isAdmin = userRole === ROLES.ADMIN;

    const columns = useMemo(
      () => [
        columnHelper.accessor("name", {
          header: () => (
            <SortableHeader
              title="Project name"
              sortKey="name"
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={onSort}
            />
          ),
          cell: (info) => (
            <div
              title={info.getValue()}
              style={{
                overflowWrap: "anywhere",
                whiteSpace: "pre-wrap",
              }}
            >
              {info.getValue()}
            </div>
          ),
          meta: { width: "236px", maxWidth: "236px" } as ColumnMeta,
        }),
        columnHelper.accessor("project_type", {
          header: () => (
            <SortableHeader
              title="Project type"
              sortKey="project_type"
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={onSort}
            />
          ),
          cell: (info) => (
            <span>{capitalizeFirstLetter(info.getValue() as string)}</span>
          ),
          meta: { width: "144px" } as ColumnMeta,
        }),
        columnHelper.accessor("client.org_name", {
          header: () => (
            <SortableHeader
              title="Organization name"
              sortKey="org_name"
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={onSort}
            />
          ),
          cell: (info) => (
            <div
              title={info.getValue()}
              style={{
                overflowWrap: "anywhere",
                whiteSpace: "pre-wrap",
              }}
            >
              {info.getValue()}
            </div>
          ),
          meta: { width: "178px", maxWidth: "178px" } as ColumnMeta,
        }),

        columnHelper.accessor("esti_hrs", {
          header: () => (
            <SortableHeader
              title="Estimated hours"
              sortKey="esti_hrs"
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={onSort}
            />
          ),
          cell: (info) => renderEstiHoursCell(info.getValue()),
          meta: { width: "144px" } as ColumnMeta,
        }),
        columnHelper.accessor("util_hrs", {
          header: () => (
            <SortableHeader
              title="Utilized hours"
              sortKey="util_hrs"
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={onSort}
            />
          ),
          cell: (info) => <HoursDisplay hours={info.getValue() || 0} />,
          meta: { width: "144px" } as ColumnMeta,
        }),
        columnHelper.accessor("remi_hrs", {
          header: () => (
            <SortableHeader
              title="Remaining hours"
              sortKey="remi_hrs"
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={onSort}
            />
          ),
          cell: (info) => {
            const isNegative = (info.getValue() as string).includes("-");
            return (
              <HoursDisplay
                hours={info.getValue() || 0}
                isNegative={isNegative}
              />
            );
          },
          meta: { width: "144px" } as ColumnMeta,
        }),
        columnHelper.accessor("start_date", {
          header: () => (
            <SortableHeader
              title="Start date"
              sortKey="start_date"
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={onSort}
            />
          ),
          cell: (info) => {
            return moment(info.getValue(), "DD/MM/YYYY").format("MM/DD/YYYY");
          },
          meta: { width: "144px" } as ColumnMeta,
        }),
        columnHelper.accessor("end_date", {
          header: () => (
            <SortableHeader
              title="End date"
              sortKey="end_date"
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={onSort}
            />
          ),
          cell: (info) => {
            const startDate = moment(info.row.original.start_date, "DD/MM/YYYY");
            const endDate = moment(info.getValue(), "DD/MM/YYYY");
            const isSameDate = startDate.isSame(endDate, "day");
            const textColor = isSameDate ? "text-[#FF3B30]" : "";
            return (
              <span className={textColor}>{(endDate.format("MM/DD/YYYY") && endDate.format("MM/DD/YYYY") !== "Invalid date") ? endDate.format("MM/DD/YYYY") : "-"}</span>
            );
          },
          meta: { width: "144px" } as ColumnMeta,
        }),
        columnHelper.accessor("status", {
          header: "Status",
          cell: (info) => {
            const status = info.getValue();
            const { bgColor, textColor } = getStatusStyles(status);
            return (
              <span
                className={`inline-block px-2 py-1 rounded-sm text-xs font-medium ${bgColor} ${textColor}`}
              >
                {capitalizeFirstLetter(status)}
              </span>
            );
          },
          meta: { width: "144px" } as ColumnMeta,
        }),
        columnHelper.display({
          id: "actions",
          header: "Action",
          cell: (info) => {
            const project = info.row.original;
            return (
              <div className="flex items-center space-x-2.5">
                <Tooltip content="View">
                  <button
                    onClick={() => onView(project.id)}
                    className="cursor-pointer relative"
                    aria-label="View project"
                  >
                    <EyeSVG />
                    {project.warning && (
                      <span className="absolute top-0 right-0 w-[8px] h-[8px] bg-[#F59E0B] rounded-full"></span>
                    )}
                  </button>
                </Tooltip>

                {isAdmin && (
                  <>
                    <Tooltip content="Edit">
                      <button
                        onClick={() => onEdit(project.id)}
                        className="cursor-pointer"
                        aria-label="Edit project"
                      >
                        <ClientEditSVG />
                      </button>
                    </Tooltip>
                    <Tooltip content="Add more time">
                      <button
                        className="cursor-pointer"
                        aria-label="Add time"
                        onClick={(e) => {
                          e.stopPropagation();
                          onAddTime(project.id);
                        }}
                      >
                        <ProjectAddMoreTimeSVG />
                      </button>
                    </Tooltip>
                    <Tooltip content="Extend end date">
                      <button
                        className="cursor-pointer"
                        aria-label="Extend time"
                        onClick={(e) => {
                          e.stopPropagation();
                          onExtend(project.id);
                        }}
                      >
                        <ProjectExtendTimeSVG />
                      </button>
                    </Tooltip>
                    <Tooltip content="Delete">
                      <button
                        className="cursor-pointer"
                        aria-label="Delete project"
                        onClick={(e) => {
                          e.stopPropagation();
                          onDelete(project.id);
                        }}
                      >
                        <ProjectDeleteSVG />
                      </button>
                    </Tooltip>
                  </>
                )}
              </div>
            );
          },
          meta: {
            width: isAdmin ? "163px" : "100px",
            isSticky: true,
          } as ColumnMeta,
        }),
      ],
      [columnHelper, onEdit, onView, sortOrder, sortBy, onSort]
    );

    const table = useReactTable({
      data,
      columns,
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
    });

    return (
      <div className="flex flex-col">
        <div className="overflow-x-auto relative py-4">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-[#F4F7F9] text-left">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  {headerGroup.headers.map((header) => {
                    const isSticky = (header.column.columnDef.meta as any)
                      ?.isSticky;
                    return (
                      <th
                        key={header.id}
                        scope="col"
                        style={{
                          width: (header.column.columnDef.meta as ColumnMeta)
                            ?.width,
                          minWidth: (header.column.columnDef.meta as ColumnMeta)
                            ?.width,
                          maxWidth: (header.column.columnDef.meta as ColumnMeta)
                            ?.width,
                        }}
                        className={`p-3 text-sm font-[600] text-[#1E1E1E] ${isSticky
                          ? "sticky right-0 bg-[#F4F7F9] z-10 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)]"
                          : ""
                          }`}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody className="bg-white divide-y divide-gray-200 border-l-1 border-r-1 border-b-1 border-gray-200">
              {data.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="text-center py-4">
                    No projects found
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="hover:bg-[#F9FEFF]">
                    {row.getVisibleCells().map((cell) => {
                      const isSticky = (cell.column.columnDef.meta as ColumnMeta)
                        ?.isSticky;
                      return (
                        <td
                          key={cell.id}
                          style={{
                            width: (cell.column.columnDef.meta as ColumnMeta)
                              ?.width,
                            minWidth: (cell.column.columnDef.meta as ColumnMeta)
                              ?.width,
                            maxWidth: (cell.column.columnDef.meta as ColumnMeta)
                              ?.width,
                          }}
                          className={`whitespace-nowrap text-sm text-[#1E1E1E] font-[500] ${isSticky
                            ? "sticky right-[-1px] my-0.5 z-10"
                            : "px-3 py-4"
                            }`}
                        >
                          {isSticky ? (
                            <div className="p-3 bg-white">
                              {flexRender(
                                cell.column.columnDef.cell,
                                cell.getContext()
                              )}
                            </div>
                          ) : (
                            flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-4 text-sm text-[#1E1E1E] font-[600] flex justify-between items-center">
          <div>
            Showing{" "}
            {pagination.currentPage === 1
              ? 1
              : (pagination.currentPage - 1) * pagination.pageSize + 1}{" "}
            to{" "}
            {Math.min(
              pagination.currentPage * pagination.pageSize,
              pagination.totalItems
            )}{" "}
            of {pagination.totalItems} results
          </div>
          <div className="flex items-center gap-4">
            <button
              onClick={() => {
                if (pagination.currentPage > 1) {
                  onPageChange(pagination.currentPage - 1);
                }
              }}
              disabled={pagination.currentPage <= 1}
              className="px-3 py-1 w-[78px] h-[34px] text-sm text-[#1E1E1E] bg-[#F5F5F5] rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
            >
              Previous
            </button>
            <button
              onClick={() => {
                if (pagination.currentPage < pagination.totalPages) {
                  onPageChange(pagination.currentPage + 1);
                }
              }}
              disabled={pagination.currentPage >= pagination.totalPages}
              className="px-3 py-1 w-[78px] h-[34px] text-sm text-white bg-[#73B5C5] rounded-md hover:bg-[#5da7b8] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    );
  };

const Projects: React.FC = () => {
  const dispatch = useAppDispatch();
  const projects = useAppSelector(selectFilteredProjects);
  const searchTerm = useAppSelector(selectSearchTerm);
  const clientId = useAppSelector(selectClientId);
  const statusFilter = useAppSelector(selectStatusFilter);
  const warningFilter = useAppSelector(selectWarningFilter);
  const projectTypeFilter = useAppSelector(selectProjectTypeFilter);
  const pagination = useAppSelector(selectPagination);
  const clientFilterList = useAppSelector(selectClientOptions);
  const isLoading = useAppSelector(selectProjectLoading);
  const sortBy = useAppSelector(selectSortBy);
  const sortOrder = useAppSelector(selectSortOrder);

  const noProjects = useAppSelector(selectNoProjects);

  const userRole = useSelector(selectUserRole);
  const isAdmin = userRole === ROLES.ADMIN;
  const isClient = userRole === ROLES.CLIENT;
  const selectedOrganization = useSelector(selectSelectedOrganization);

  // Track the currently applied filter values for reset functionality
  const [currentClientId, setCurrentClientId] = useState("All clients");
  const [currentStatusFilter, setCurrentStatusFilter] =
    useState("All statuses");

  // Use ref to track if initial data has been loaded - MUST be before useEffects that use it
  const initialDataLoaded = useRef(false);

  // Update filter state and trigger API call when filters change
  useEffect(() => {
    // Only trigger API call if the component has mounted (prevents double API call on init)
    if (initialDataLoaded.current) {
      setCurrentClientId(clientId);
      setCurrentStatusFilter(statusFilter);

      dispatch(
        fetchProjects({
          page: 1, // Reset to first page when applying filters
          pageSize: pagination.pageSize,
          search: searchTerm || undefined,
          clientId: clientId !== "All clients" ? clientId : undefined,
          status:
            statusFilter !== "All statuses"
              ? statusFilter.toLowerCase()
              : undefined,
          sortBy: sortBy || undefined,
          sortOrder: sortOrder || undefined,
        })
      );
    }
    // Note: pagination.pageSize excluded to prevent infinite loops (fetchProjects updates pagination)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    clientId,
    statusFilter,
    warningFilter,
    projectTypeFilter,
    dispatch,
    sortBy,
    sortOrder,
    selectedOrganization, // Refetch when client switches organization
  ]);

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isExtendDateModalOpen, setIsExtendDateModalOpen] = useState(false);
  const [isAddMoreTimeModalOpen, setIsAddMoreTimeModalOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState<ProjectList | null>(
    null
  );
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    null
  );
  // State variables for modals have been removed as they're now managed by React Hook Form

  // Handle sort changes
  const handleSort = (key: string) => {
    let newSortOrder: "asc" | "desc" = "asc";
    if (sortBy === key && sortOrder === "asc") {
      newSortOrder = "desc";
    } else if (sortBy === key && sortOrder === "desc") {
      newSortOrder = "asc";
    }

    // If we want to toggle off:
    // asc -> desc -> undefined
    // For now, let's just toggle asc <-> desc if same key, or restart at asc
    // Actually common UX: Click -> Asc, Click -> Desc, Click -> Off/Asc?
    // User requirement just said "display it have siortying".
    // I'll stick to Asc <-> Desc. 

    // Wait, if I want to support unsort:
    if (sortBy === key && sortOrder === "desc") {
      // Toggle to off? Or back to asc?
      // Let's implement: Asc -> Desc -> Asc
      newSortOrder = "asc";
    }

    dispatch(setSort({ sortBy: key, sortOrder: newSortOrder }));

    // API call will be triggered by useEffect dependency on sortBy/sortOrder
  };

  // Open extend end date modal
  const handleExtendEndDate = (projectId: string) => {
    const project = projects.find((p) => p.id === projectId);
    if (project) {
      setSelectedProject(project);
      setIsExtendDateModalOpen(true);
    }
  };

  // Open add more time modal
  const handleAddMoreTime = (projectId: string) => {
    const project = projects.find((p) => p.id === projectId);
    if (project) {
      setSelectedProject(project);
      setIsAddMoreTimeModalOpen(true);
    }
  };

  // Create a debounced search function - only for search, not for role/status filters
  const debouncedSearch = useCallback(
    debounce((searchValue: string) => {
      dispatch(
        fetchProjects({
          page: 1, // Reset to first page when searching
          pageSize: pagination.pageSize,
          search: searchValue || undefined,
          clientId: clientId !== "All clients" ? clientId : undefined,
          status:
            statusFilter !== "All statuses"
              ? statusFilter.toLowerCase()
              : undefined,
        })
      );
    }, 500),
    [
      dispatch,
      pagination.pageSize,
      clientId,
      statusFilter,
      warningFilter,
      projectTypeFilter,
    ]
  );

  // Fetch projects and clients on component mount
  useEffect(() => {
    if (!initialDataLoaded.current) {
      const initialQuery: ProjectQuery = {
        page: 1,
        pageSize: 100,
      };
      dispatch(fetchProjects(initialQuery));
      dispatch(fetchClientOptions());
      initialDataLoaded.current = true;
    }

    // Reset filters when component unmounts
    return () => {
      dispatch(resetFilters());
    };
  }, [dispatch]);

  // Handle search term changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    dispatch(setSearchTerm(value));
    debouncedSearch(value);
  };

  // Reset filters - used in the reset filters button
  const handleResetFilters = () => {
    dispatch(resetFilters());

    // Reset current filters to default values
    setCurrentClientId("All clients");
    setCurrentStatusFilter("All statuses");

    // Fetch all projects without filters
    dispatch(fetchProjects({ page: 1, pageSize: 100 }));
  };

  // Open add project modal
  const handleAddProject = () => {
    setSelectedProject(null);
    setIsAddModalOpen(true);
  };

  // Open edit project modal
  const handleEditProject = (projectId: string) => {
    // Fetch the latest project details from the API instead of using table data
    dispatch(getProjectDetail(projectId))
      .unwrap()
      .then((projectData: ProjectList) => {
        setSelectedProject(projectData as ProjectList);
        setIsEditModalOpen(true);
      })
      .catch((error: Error) => {
        console.error("Failed to fetch project details:", error);
      });
  };

  // Open view project modal
  const handleViewProject = (projectId: string) => {
    // Fetch the latest project details from the API instead of using table data
    dispatch(getProjectDetail(projectId))
      .unwrap()
      .then((projectData: ProjectList) => {
        setSelectedProject(projectData as ProjectList);
        setIsViewModalOpen(true);
      })
      .catch((error: Error) => {
        console.error("Failed to fetch project details:", error);
      });
  };

  // Handle form submission for adding a project
  const handleAddFormSubmit = (data: ProjectFormData) => {
    const baseProject = {
      name: data.name,
      client_id: data.clientId,
      org_id: data.orgId, // Add org_id to payload
      email: data.email,
      start_date: moment(data.startDate, "YYYY-MM-DD").format("DDMMYYYY"),
      user_ids: data.assignedTo,
      notes: data.notes,
      project_type: data.projectType,
    } as any;

    const newProject = {
      ...baseProject,
      ...(data.projectType === "block of time"
        ? {
          end_date: moment(data.endDate, "YYYY-MM-DD").format("DDMMYYYY"),
          esti_hrs: data.estimatedHours,
        }
        : {}),
    };
    dispatch(addProject(newProject as ProjectList))
      .unwrap()
      .then(() => {
        setIsAddModalOpen(false);
        setSelectedProject(null);
      })
      .catch((error) => {
        console.error("Failed to add project:", error);
        // Modal stays open on error
      });
  };

  // Handle form submission for editing a project
  const handleEditFormSubmit = (data: ProjectFormData) => {
    if (selectedProject) {
      const baseUpdated: Omit<
        ProjectList,
        | "id"
        | "client"
        | "tracker"
        | "assignees"
        | "is_active"
        | "util_hrs"
        | "remi_hrs"
      > & {
        status: "active" | "completed";
      } = {
        ...selectedProject,
        name: data.name,
        client_id: data.clientId,
        org_id: data.orgId, // Add org_id to payload
        email: data.email,
        start_date: moment(data.startDate, "YYYY-MM-DD").format("DDMMYYYY"),
        user_ids: data.assignedTo,
        notes: data.notes,
        status: (data.status as any) || (selectedProject.status as any),
        project_type: (data.projectType as any) || ((selectedProject as any).project_type as any),
      } as any;

      // Ensure end_date and esti_hrs are only present for 'block of time'
      const updatedProject: any = { ...baseUpdated };
      if (data.projectType === "block of time") {
        updatedProject.end_date = moment(data.endDate, "YYYY-MM-DD").format("DDMMYYYY");
        updatedProject.esti_hrs = data.estimatedHours;
      } else {
        delete updatedProject.end_date;
        delete updatedProject.esti_hrs;
      }
      dispatch(updateProject(updatedProject as ProjectList))
        .unwrap()
        .then(() => {
          setIsEditModalOpen(false);
          setSelectedProject(null);
        })
        .catch((error) => {
          console.error("Failed to update project:", error);
          // Modal stays open on error
        });
    }
  };

  return (
    <AppLayout
      title="Projects"
      rightActions={
        <>
          {isAdmin && (
            <button
              onClick={handleAddProject}
              className="bg-[#73B5C5] hover:bg-[#5da7b8] text-white px-4 py-2 rounded-md text-sm flex items-center cursor-pointer gap-1"
            >
              Add project
              <PlusIconSVG />
            </button>
          )}
        </>
      }
      contentClassName={`min-h-[90%] ${pagination.totalPages ? "" : " items-center justify-center"
        }`}
    >
      <div className="p-4">
        {noProjects ? (
          <NoDataContainer
            title="No projects yet!"
            description="Start by creating a new project to track projects effectively."
            buttonLabel={isAdmin ? "Add project" : undefined}
            onButtonClick={isAdmin ? handleAddProject : undefined}
          />
        ) : (
          <div className="bg-white rounded-lg">
            {/* Search and filters */}
            <div className="py-4 flex flex-wrap items-center justify-between gap-2">
              <div className="relative flex-grow max-w-[246px]">
                <input
                  type="text"
                  placeholder="Search"
                  className="w-full h-[42px] px-3 pl-9 bg-[#F9FBFF] rounded-[6px] focus:outline-none focus:border-[#73B5C5] placeholder:text-[#B5B7C0]"
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-2.5 pointer-events-none">
                  <InputSearchIconSVG />
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <div className="flex gap-3 mr-3">
                  <div className="min-w-[143px]">
                    <FilterSelectWithRadio
                      options={[
                        { value: "All projects", label: "All projects" },
                        { value: "true", label: "With warning" },
                        { value: "false", label: "Without warning" },
                      ]}
                      width="w-[211px]"
                      maxHeight="250px"
                      value={warningFilter}
                      onChange={(value) => dispatch(setWarningFilter(value))}
                    />
                  </div>
                  <div className="min-w-[143px]">
                    <FilterSelectWithRadio
                      options={[
                        {
                          value: "All project types",
                          label: "All project types",
                        },
                        { value: "Billable", label: "Billable" },
                        { value: "Non-billable", label: "Non-billable" },
                        { value: "Block of time", label: "Block of time" },
                      ]}
                      value={projectTypeFilter}
                      onChange={(value) =>
                        dispatch(setProjectTypeFilter(value))
                      }
                    />
                  </div>
                  <div className="min-w-[143px]">
                    <FilterSelectWithRadio
                      options={[
                        { value: "All clients", label: "All organizations" },
                        ...clientFilterList.map((client) => ({
                          value: client.id, // Use org id instead of org_name
                          label: capitalizeFirstLetter(client.org_name),
                        })),
                      ]}
                      width="w-[211px]"
                      maxHeight="250px"
                      showSearch
                      searchPlaceholder="Search organizations"
                      value={clientId}
                      onChange={(value) => dispatch(setClientId(value))}
                    />
                  </div>
                  <div className="min-w-[143px]">
                    <FilterSelectWithRadio
                      options={[
                        { value: "All statuses", label: "All statuses" },
                        { value: "Active", label: "Active" },
                        { value: "Completed", label: "Completed" },
                      ]}
                      value={statusFilter}
                      onChange={(value) => dispatch(setStatusFilter(value))}
                    />
                  </div>
                </div>

                <button
                  className={`h-[38px] px-4 text-sm text-[#6C6C6C] bg-white rounded-[6px] ${currentClientId !== "All clients" ||
                    currentStatusFilter !== "All statuses" ||
                    warningFilter !== "All projects" ||
                    projectTypeFilter !== "All project types"
                    ? "hover:bg-gray-50 cursor-pointer"
                    : "opacity-50 cursor-not-allowed"
                    }`}
                  onClick={handleResetFilters}
                  disabled={
                    currentClientId === "All clients" &&
                    currentStatusFilter === "All statuses" &&
                    warningFilter === "All projects" &&
                    projectTypeFilter === "All project types"
                  }
                >
                  Reset filter
                </button>
              </div>
            </div>

            {/* Project table or empty state */}

            <ProjectTable
              pagination={pagination}
              onPageChange={(page) => {
                dispatch(
                  fetchProjects({
                    page,
                    pageSize: pagination.pageSize,
                  })
                );
              }}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSort={handleSort}
              data={projects}
              onEdit={handleEditProject}
              onView={handleViewProject}
              onExtend={handleExtendEndDate}
              onAddTime={handleAddMoreTime}
              onDelete={(projectId: string) => {
                setSelectedProjectId(projectId);
                setIsDeleteModalOpen(true);
              }}
            />
          </div>
        )}

        {/* Add Project Modal */}
        <Modal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          title="Add project"
          width="min(95%, 868px)"
          contentClassName="p-0"
        >
          <ProjectForm
            onSubmit={handleAddFormSubmit}
            onCancel={() => setIsAddModalOpen(false)}
          />
        </Modal>

        {/* Edit Project Modal */}
        {selectedProject && (
          <Modal
            isOpen={isEditModalOpen}
            onClose={() => setIsEditModalOpen(false)}
            title="Edit project"
            width="min(95%, 868px)"
            contentClassName="p-0"
          >
            <ProjectForm
              initialData={selectedProject}
              onSubmit={handleEditFormSubmit}
              onCancel={() => setIsEditModalOpen(false)}
            />
          </Modal>
        )}

        {/* View Project Modal */}
        {selectedProject && (
          <Modal
            isOpen={isViewModalOpen}
            onClose={() => setIsViewModalOpen(false)}
            title="View project"
            width="min(95%, 868px)"
            contentClassName="p-0"
          >
            <ProjectForm
              initialData={selectedProject}
              onSubmit={() => { }}
              onCancel={() => setIsViewModalOpen(false)}
              viewMode={true}
            />
          </Modal>
        )}

        {/* Delete Confirmation Modal */}

        {selectedProjectId && (
          <Modal
            isOpen={isDeleteModalOpen}
            onClose={() => setIsDeleteModalOpen(false)}
            title="Delete project"
            width="min(95%, 550px)"
          >
            <div className="py-2">
              <p className="text-center text-lg mb-6">
                Are you sure you want to delete the project?
              </p>

              <div className="flex justify-center gap-4">
                <button
                  onClick={() => {
                    if (selectedProjectId) {
                      dispatch(deleteProject(selectedProjectId))
                        .unwrap()
                        .then(() => {
                          setIsDeleteModalOpen(false);
                        });
                    }
                  }}
                  className="px-3 py-1  w-[98px] h-[36px] text-sm text-white bg-[#73B5C5] rounded-md hover:bg-[#5da7b8] cursor-pointer flex items-center justify-center"
                >
                  {isLoading ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Deleting...
                    </>
                  ) : (
                    "Yes"
                  )}
                </button>
                <button
                  onClick={() => setIsDeleteModalOpen(false)}
                  className="px-3 py-1 w-[98px] h-[36px] text-sm text-[#1E1E1E] bg-[#F5F5F5] rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                >
                  Cancel
                </button>
              </div>
            </div>
          </Modal>
        )}

        {/* Extend End Date Modal */}
        {selectedProject && (
          <ExtendEndDateModal
            isOpen={isExtendDateModalOpen}
            onClose={() => setIsExtendDateModalOpen(false)}
            project={selectedProject}
            isLoading={isLoading}
          />
        )}

        {/* Add More Time Modal */}
        {selectedProject && (
          <AddMoreTimeModal
            isOpen={isAddMoreTimeModalOpen}
            onClose={() => setIsAddMoreTimeModalOpen(false)}
            project={selectedProject}
            isLoading={isLoading}
          />
        )}
      </div>
    </AppLayout>
  );
};

export default Projects;
